import React, { memo } from 'react';
import PropTypes from 'prop-types';
import { Map } from 'immutable';
import classNames from 'classnames';

import FormGroup from '../../helpers/FormGroup';
import BaseInput from '../../helpers/Input';
import BaseSelect from '../../helpers/Select';
import DropDown from '../../helpers/DropDown/Changable';
import { WebInput } from '../../helpers/WebInput';
import BaseAutoComplete from '../../helpers/AutoComplete';
import { PhoneInput } from '../../helpers/PhoneInput';
import { unwrap } from '../../../utils/ChangeSpy';
import PrettyErrorDecorator from '../../decorators/PrettyError';
import DisplayError from '../../decorators/DisplayError';
import { getTickerExchange, hasTickerExchange } from '../getTickerExchange';
import canHasParent from '../canHasParent';
import { LocalTimeZone } from '../../LocalTimeZone';

import style from './style.module.css';

const Input = PrettyErrorDecorator(DisplayError(BaseInput));
const Select = PrettyErrorDecorator(DisplayError(BaseSelect));
const AutoComplete = PrettyErrorDecorator(DisplayError(BaseAutoComplete));

/**
 * Stateless Company Information component.
 *
 * @param props {Object}.
 * @param props.targetCompany {Immutable.Map} Map with company information.
 * @param props.onCompanyInfoChange {Function} Handle company info change event.
 * @param props.onToggleDBA {Function} Handle toggle dba input event.
 */
const CompanyInfoComponent = ({
  canEditData,
  targetCompany,
  companyInfoProps,
  suggestionProps,
  countrySuggestion,
  onErrorClose,
  error,
  onBlur,
}) => {
  const { onCompanyInfoChange, onToggleDBA, onParentCompanyClick } = companyInfoProps;

  const info = targetCompany.get('info');
  const states = targetCompany.get('states');
  const entityTypes = targetCompany.get('entityTypes').map(type => Map({ name: type, value: type }));
  const companyStates = states.map(state => Map({ name: state, value: state }));
  const parentCompany = targetCompany.get('parentCompany');

  const legalName = info.get('legalName');
  const abbrName = info.get('abbrName');
  const dbaName = info.get('dbaName');
  const web = info.get('web');
  const phone = info.get('phone');
  const address = info.get('address');
  const city = info.get('city');
  const countryName = info.getIn(['country', 'countryName']);
  const companyState = info.get('state');
  const entityType = info.get('companyType');
  const zip = info.get('zip');
  const showDBA = info.get('showDBA');
  const toggleDBAButton = (
    <i aria-hidden="true" className={classNames('fa', { 'fa-minus': showDBA, 'fa-plus': !showDBA })} />
  );
  const dbaClass = showDBA ? '' : 'hidden';
  const showCountry = info.get('showCountry');
  const parentCompanyClass = canHasParent(entityType) ? '' : 'hidden';
  const selectedCompany = parentCompany.get('selected');
  const selectedCompanyInfo = selectedCompany ? <div>{parentCompany.getIn(['shortInfoText', 'value'], '')}</div> : null;
  const Ticker = getTickerExchange(info);
  const countryClassName = classNames(style.country, { hidden: !showCountry });
  // Abbr, DBA, Type and Ticker fill half-width cells; Web takes the whole row when it would be left alone.
  const webClassName = classNames(style.web, { [style.wide]: Boolean(showDBA) === hasTickerExchange(info) });

  // When DBA is filled, don't allow users to close DBA field
  const dba = unwrap(dbaName);
  const toggleDBAClass = classNames('input-group-addon', {
    [style.dbaDisabled]: dba && showDBA,
    [style.clickable]: !(dba && showDBA),
  });

  const dbaToggle = event => {
    if (dba && showDBA) return;
    onToggleDBA(event);
  };

  const gMapAddress = `//maps.google.com/?q=${address.get('value')},${city.get('value')},${companyState.get(
    'value',
  )},${zip.get('value')},${countryName.get('value')}`;
  const timezone = info.get('timezone');

  return (
    <form className={style.host} onChange={onCompanyInfoChange}>
      <FormGroup className={style.wide}>
        <Input
          disabled={!canEditData}
          error={error.legalName}
          id="legalName"
          label="Name"
          name="legalName"
          onBlur={onBlur}
          placeholder="Full Name"
          value={legalName}
          canDisplayError
          showLabel
        />
      </FormGroup>
      <FormGroup className="input-group abbr">
        <Input
          disabled={!canEditData}
          id="abbrName"
          label="Abbreviated Name"
          name="abbrName"
          placeholder="Abbreviated Name"
          value={abbrName}
          showLabel
        />
        <span className={toggleDBAClass} onClick={dbaToggle} title={showDBA ? 'Hide DBA/Alt' : 'Add DBA/Alt'}>
          {toggleDBAButton}
        </span>
      </FormGroup>
      <FormGroup className={dbaClass}>
        <Input
          disabled={!canEditData}
          id="dbaName"
          label="DBA/Alt"
          name="dbaName"
          placeholder="DBA/Alt"
          value={dbaName}
          showLabel
        />
      </FormGroup>
      <FormGroup>
        <Select
          className="form-control"
          disabled={!canEditData}
          id="dropdownType"
          label="Type"
          name="companyType"
          options={entityTypes}
          value={entityType}
          showLabel
        />
      </FormGroup>
      <div className={style.ticker}>
        <Ticker company={info} exchangeName="tickerExchange" onChange={onCompanyInfoChange} symbolName="tickerSymbol" />
      </div>
      <div className={webClassName}>
        <label className="field-label" htmlFor="web">
          Web
        </label>
        <WebInput
          disabled={!canEditData}
          id="web"
          maxLength={150}
          name="web"
          placeholder="Web"
          type="web"
          value={web}
        />
      </div>
      <div className={classNames(style.wide, parentCompanyClass)}>
        <label className="field-label">Parent Company</label>
        <AutoComplete {...suggestionProps}>
          <span
            className={classNames('input-group-addon', style.clickable)}
            onClick={onParentCompanyClick}
            title="Open parent company"
          >
            <i aria-hidden="true" className="fa fa-external-link" />
          </span>
        </AutoComplete>
        {selectedCompanyInfo}
      </div>
      <div className={classNames(style.wide, style.addressRow)}>
        <FormGroup className={style.addressGroup}>
          <div className="form-group input-group">
            <Input
              autoComplete="off"
              disabled={!canEditData}
              id="address"
              label="Address"
              name="address"
              placeholder="Address"
              value={address}
              showLabel
            />
            <span className="input-group-addon">
              <a href={gMapAddress} rel="noopener noreferrer" target="_blank">
                <i className="fa fa-map" title="Open address in a new tab" />
              </a>
            </span>
          </div>
        </FormGroup>
        <FormGroup>
          <Input disabled={!canEditData} id="city" label="City" name="city" placeholder="City" value={city} showLabel />
        </FormGroup>
        <FormGroup className={style.state}>
          <label className="field-label">State</label>
          <DropDown
            className="form-control"
            disabled={!canEditData}
            displayAll={false}
            id="dropdownMenu2"
            label="State"
            name="state"
            onChange={onCompanyInfoChange}
            options={companyStates}
            value={companyState}
            allowEmptyValue
          />
        </FormGroup>
        <FormGroup>
          <Input
            disabled={!canEditData}
            error={error.zip}
            id="zip"
            label="Zip"
            name="zip"
            onBlur={onBlur}
            placeholder="Zip"
            value={zip}
            canDisplayError
            showLabel
          />
        </FormGroup>
      </div>
      <div className={classNames(style.wide, style.contactRow)}>
        <FormGroup>
          <PhoneInput
            disabled={!canEditData}
            error={error.phone}
            maxLength="30"
            name="phone"
            onBlur={onBlur}
            onErrorClose={event => {
              onErrorClose(event, 'phone');
            }}
            placeholder="Phone"
            value={unwrap(phone)}
          />
        </FormGroup>
        <FormGroup className={countryClassName} title="ONLY enter for countries outside the U.S. and Canada">
          <label className="field-label">Country</label>
          {countrySuggestion}
        </FormGroup>
        <div className={style.localTime}>
          <span className="field-label">Local Time</span>
          <LocalTimeZone format="h:mm A" timezone={timezone} showTimezoneAbbreviation />
        </div>
      </div>
    </form>
  );
};

CompanyInfoComponent.propTypes = {
  targetCompany: PropTypes.instanceOf(Map).isRequired,
};

/** Company info component. */
export const CompanyInfo = memo(CompanyInfoComponent);
